import { ApiError, fileUrl } from '../../api/client';
import type { RemoteFileSource } from '../../api/fileBrowserUpload';

export const FILE_CLIPBOARD = 'application/x-skrepka-files';
const TEXT_PREFIX = 'skrepka-files-v1:';
export interface FileReference { assetId: number; pathName: string; path: string; name: string; size: number; modified: number }
let copied: FileReference[] = [];
export const getCopiedFiles = () => copied.slice();
export function copyFiles(files: FileReference[]) { copied = files.map(file => ({ ...file })); }
export function encodeFiles(files: FileReference[]) { return TEXT_PREFIX + JSON.stringify({ origin: window.location.origin, files }); }
export function decodeFiles(raw: string): FileReference[] {
  if (!raw.startsWith(TEXT_PREFIX) || raw.length > 256 * 1024) return [];
  try {
    const value: unknown = JSON.parse(raw.slice(TEXT_PREFIX.length));
    if (!value || typeof value !== 'object' || !('origin' in value) || value.origin !== window.location.origin || !('files' in value) || !Array.isArray(value.files) || !value.files.length || value.files.length > 1000) return [];
    const segment = (s: unknown): s is string => typeof s === 'string' && s.length > 0 && s.length <= 1024 && ![...s].some(c => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127 || c === '/' || c === '\\') && s !== '.' && s !== '..';
    const valid = (item: unknown): item is FileReference => {
      if (!item || typeof item !== 'object') return false;
      const f = item as Partial<FileReference>;
      return typeof f.assetId === 'number' && Number.isSafeInteger(f.assetId) && f.assetId > 0 && segment(f.pathName) && segment(f.name) && typeof f.path === 'string' && f.path.length <= 8192 && f.path.split('/').every(segment) && f.path.split('/').at(-1) === f.name && typeof f.size === 'number' && Number.isSafeInteger(f.size) && f.size >= 0 && typeof f.modified === 'number' && Number.isFinite(f.modified) && f.modified >= 0;
    };
    const files: unknown[] = value.files;
    if (!files.every(valid)) return [];
    return files.map(f => ({ assetId: f.assetId, pathName: f.pathName, path: f.path, name: f.name, size: f.size, modified: f.modified }));
  } catch { return []; }
}
export function isEditable(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest('input:not([type="checkbox"]),textarea,select,[contenteditable]:not([contenteditable="false"]),[role="textbox"],[data-file-paste-ignore]'));
}
export function hasFiles(data: DataTransfer) { return Array.from(data.types).includes('Files'); }
export function droppedFiles(data: DataTransfer): File[] {
  const items = Array.from(data.items || []).filter(item => item.kind === 'file');
  if (items.some(item => item.webkitGetAsEntry?.()?.isDirectory)) throw new Error('Выберите файлы внутри папки. Перетаскивание папок пока не поддерживается.');
  return data.files.length ? Array.from(data.files) : items.map(item => item.getAsFile()).filter((file): file is File => file !== null);
}

/** Copies original bytes via the separate files service, at most 1 MiB at a time.
 * The ordinary user's source/destination permissions are checked by each server request.
 */
export function remoteFile(reference: FileReference): RemoteFileSource {
  return remoteOriginal(fileUrl(reference.assetId, reference.pathName, reference.path, true), reference);
}

/** URL is built locally from validated location IDs, never copied from clipboard. */
export function remoteOriginal(url: string, reference: { name: string; size: number; modified: number }): RemoteFileSource {
  let etag: string | null = null, modified: string | null = null, initialized = false;
  const changed = () => new ApiError(`Файл «${reference.name}» изменился. Скопируйте его заново.`, 409);
  async function read(start?: number, end?: number, signal?: AbortSignal): Promise<Blob> {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.throwIfAborted(); signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 120000);
    try {
      const partial = start !== undefined && end !== undefined;
      const headers: Record<string, string> = partial ? { Range: `bytes=${start}-${end - 1}` } : {};
      if (initialized && etag) headers['If-Match'] = etag;
      const response = await fetch(url, { method: partial ? 'GET' : 'HEAD', credentials: 'same-origin', redirect: 'error', cache: 'no-store', headers, signal: controller.signal });
      const fail = async (error: Error): Promise<never> => { await response.body?.cancel(); throw error; };
      if (response.status === 412 || response.status === 416) return await fail(changed());
      if (!response.ok) return await fail(new ApiError(`Не удалось прочитать «${reference.name}» (HTTP ${response.status}).`, response.status));
      const currentTag = response.headers.get('etag'), currentModified = response.headers.get('last-modified');
      if (!currentModified || Math.floor(Date.parse(currentModified) / 1000) !== Math.floor(reference.modified) || (initialized && (etag !== currentTag || modified !== currentModified))) return await fail(changed());
      if (!partial) {
        if (response.headers.get('content-length') !== String(reference.size)) return await fail(changed());
        etag = currentTag; modified = currentModified; initialized = true;
        return new Blob([]);
      }
      const expected = end - start;
      if (expected <= 0 || expected > 1024 * 1024 || response.status !== 206 || response.headers.get('content-range') !== `bytes ${start}-${end - 1}/${reference.size}` || response.headers.get('content-length') !== String(expected)) return await fail(new ApiError('Сервер не подтвердил фрагмент исходного файла.', 409));
      const reader = response.body?.getReader();
      if (!reader) throw new ApiError('Сервер не вернул содержимое файла.', 502);
      const chunks: BlobPart[] = []; let length = 0;
      try {
        for (;;) {
          const part = await reader.read(); if (part.done) break;
          length += part.value.byteLength;
          if (length > expected) throw new ApiError('Неверный размер фрагмента исходного файла.', 409);
          chunks.push(part.value);
        }
      } finally { await reader.cancel(); reader.releaseLock(); }
      if (length !== expected) throw new ApiError('Связь прервалась при чтении исходного файла.', 0);
      return new Blob(chunks);
    } catch (error) {
      signal?.throwIfAborted();
      if (error instanceof ApiError) throw error;
      throw new ApiError('Не удалось прочитать исходный файл. Проверьте связь и повторите.', 0);
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  }
  return { name: reference.name, size: reference.size, validate: async signal => { await read(undefined, undefined, signal); }, readChunk: (start, end, signal) => read(start, end, signal) };
}
